import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';

export default function Header() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsDropdownOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-pure-white/80 dark:bg-space-black/80 backdrop-blur-md border-b border-border-gray dark:border-dark-gray">
      <div className="section-container">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/images/moldclaw-logo.png" 
              alt="moldClaw" 
              className="w-8 h-8 rounded-lg"
            />
            <span className="font-bold text-xl text-gray-900 dark:text-white">moldClaw</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-gray-700 dark:text-gray-300 dark:text-zinc-400 hover:text-gray-900 dark:text-white dark:hover:text-pure-white transition-colors">
              홈
            </Link>
            <a href="#features" className="text-gray-700 dark:text-gray-300 dark:text-zinc-400 hover:text-gray-900 dark:text-white dark:hover:text-pure-white transition-colors">
              기능
            </a>
            <a href="#usecases" className="text-gray-700 dark:text-gray-300 dark:text-zinc-400 hover:text-gray-900 dark:text-white dark:hover:text-pure-white transition-colors">
              사용 사례
            </a>
          </nav>

          {/* Theme Toggle + Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-dark-gray transition-colors"
              aria-label={theme === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            >
              {theme === 'dark' ? (
                <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 p-1 rounded-full hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-dark-gray transition-colors"
                >
                  <img
                    src={user.avatar_url || '/default-avatar.svg'}
                    alt={user.display_name || '프로필'}
                    className="w-8 h-8 rounded-full object-cover bg-gray-100 dark:bg-zinc-800"
                  />
                  <svg className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-pure-white dark:bg-zinc-800 border border-border-gray dark:border-zinc-700 rounded-lg shadow-lg py-1">
                    <div className="px-4 py-2 border-b border-border-gray dark:border-zinc-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white dark:text-pure-white">{user.display_name || user.email}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setIsDropdownOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 dark:text-zinc-400 hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                    >
                      내 정보
                    </Link>
                    <Link
                      to="/download"
                      onClick={() => setIsDropdownOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 dark:text-zinc-400 hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                    >
                      다운로드
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                    >
                      로그아웃
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="btn-ghost">
                  로그인
                </Link>
                <Link to="/register" className="btn-primary">
                  시작하기
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-gray-700 dark:text-gray-300 dark:text-zinc-400 hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-dark-gray rounded-lg"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border-gray dark:border-dark-gray">
            <nav className="flex flex-col gap-2">
              {/* Theme Toggle for Mobile */}
              <button
                onClick={toggleTheme}
                className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-300 dark:text-zinc-400 hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-dark-gray rounded-lg"
              >
                {theme === 'dark' ? (
                  <>
                    <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    라이트 모드로 전환
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                    다크 모드로 전환
                  </>
                )}
              </button>
              <hr className="my-2 border-border-gray dark:border-dark-gray" />
              <Link to="/" className="px-4 py-2 text-gray-700 dark:text-gray-300 dark:text-zinc-400 hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-dark-gray rounded-lg">
                홈
              </Link>
              <a href="#features" className="px-4 py-2 text-gray-700 dark:text-gray-300 dark:text-zinc-400 hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-dark-gray rounded-lg">
                기능
              </a>
              <a href="#usecases" className="px-4 py-2 text-gray-700 dark:text-gray-300 dark:text-zinc-400 hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-dark-gray rounded-lg">
                사용 사례
              </a>
              <hr className="my-2 border-border-gray dark:border-dark-gray" />
              {user ? (
                <>
                  <Link to="/profile" className="px-4 py-2 text-gray-700 dark:text-gray-300 dark:text-zinc-400 hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-dark-gray rounded-lg">
                    내 정보
                  </Link>
                  <Link to="/download" className="px-4 py-2 text-gray-700 dark:text-gray-300 dark:text-zinc-400 hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-dark-gray rounded-lg">
                    다운로드
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-left px-4 py-2 text-red-600 dark:text-red-400 hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-dark-gray rounded-lg"
                  >
                    로그아웃
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="px-4 py-2 text-gray-700 dark:text-gray-300 dark:text-zinc-400 hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-dark-gray rounded-lg">
                    로그인
                  </Link>
                  <Link to="/register" className="px-4 py-2 text-space-blue font-medium hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-dark-gray rounded-lg">
                    시작하기
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
